import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Insurance Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts` writes
 * every Industry Focus entry from one sentence template, and Insurance was one of the
 * six still carrying it: a two-paragraph series description under an amber note saying
 * the sector's own words had never been supplied. They were supplied on 2026-08-28 —
 * *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını tasarla"*, the same words
 * the Logistics, Manufacturing, Pharmaceuticals, Public Services, Retail, Services,
 * Technology and Tourism cuts arrived in — with a four-part full-page capture of
 * digitopia.co's Insurance Industry Focus page as the reference, plus four files: the
 * report mockup, Figure 1, and two overlapping crops of the cover. So this is the
 * thirtieth designed report page and the fifteenth focus cut to arrive with **both** of
 * its figures.
 *
 * **The shape is the reference's, band for band, and it is the folded focus shape
 * exactly**: hero → DAIMI Findings beside Figure 1 → the analysis strip with the
 * mockup → the dark methodology band → the global-perspective card → the download
 * close. So there is no component here at all, only this module and the dozen-line
 * adapter in `pages/DaimiInsurance.tsx`, which is what the fold in
 * `pages/FocusPage.tsx` was for.
 *
 * **Where the words come from, band by band.** Everything here is transcribed from the
 * reference capture. The *shape* of the page is certain; the *wording* is a careful
 * reading of body copy that renders a few pixels tall. Every band that carries prose
 * therefore carries a `check`, exactly as `content/daimiLogistics.ts` does, and
 * `daimiInsuranceChecks()` feeds them into the same manifest as the rest of the site's
 * amber notes so a production build cannot ship them unread.
 *
 * **The one thing on this page that is decided rather than transcribed** is the closing
 * band's destination, and it was decided in the request: *"en aşağıda önceden
 * konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. That band is
 * `content/reportClosing.ts`, shared by every report page but three, and it already
 * points at the Digital & AI Maturity Report 2026 — so this page inherits the answer
 * rather than restating it, and the reference capture agrees: its Global Perspective
 * card names the 2026 report too, and names this cut "The Insurance Focus Report",
 * which is the `shortName` `focus()` already gives it.
 *
 * **What the live page has that this page cannot.** Its Download buttons open an
 * embedded HubSpot form — the fourth capture is that form, filled in. These documents
 * are prerendered with no client bundle at all (see `src/prerender.tsx`), so an
 * embedded form script would never run, and the other reports on this site hand the
 * reader the PDF from our own bucket instead. So every Download button here is that
 * PDF, and `DAIMI_IS_DOWNLOAD.check` is what keeps the question of gating open.
 *
 * **Not to be confused with the Future 2030 Insurance outlook**, which is a separate
 * report with its own slug, its own cover and its own designed page
 * (`content/outlookInsurance.ts`, `pages/OutlookInsurance.tsx`) — which is why the
 * constants here are `DAIMI_IS_*` and that page's are `OUTLOOK_IN_*`. Two reports, two
 * pages, one sector. That page's `DAIMI_IN_SLUG` is the slug below, written out
 * because this module did not exist when it was needed; the two have to stay in step.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The slug is unchanged from the entry `reports.ts` has listed since the index was
 * built, and it is the one the request named: it is what digitopia.co serves, and
 * tidying it would break the inbound links this page exists to keep working.
 */
export const DAIMI_IS_SLUG = 'digital-and-ai-maturity-report-2026-insurance-industry'

/**
 * The hero's three paragraphs, read off the reference capture.
 *
 * Three, like the Airlines and Banking cuts and unlike Logistics: the third is
 * `FOCUS_CLOSE` in `reports.ts` word for word — the series' own closing line, which
 * the live Insurance hero closes with too. Written out rather than imported so that
 * rewording the series line for the other cuts cannot silently reword the page it
 * appears on.
 *
 * The 2.99 is set in bold on the live page. It is plain here, because this page renders
 * its paragraphs as strings rather than as markup — and it is the one number the page
 * can check against itself: it is also the 2025 average in Figure 1.
 */
export const DAIMI_IS_INTRO = {
  body: [
    'The insurance industry leads the cross-industry baseline with a score of 2.99, leveraging its heritage in risk discipline to build solid maturity foundations. However, a “structural tension” exists between advanced analytics and end-to-end digital execution.',
    'To win in 2026, insurers must overcome “legacy gravity” and data fragmentation to turn insights into automated, AI-driven workflows.',
    'This report outlines the sector’s maturity, leading strategies, and actions to accelerate transformation.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card in the
   * reports grid carries — and the file attached with this request is the same artwork
   * much larger, so it replaced it under the same key rather than landing beside it
   * under a second name. The index card reads the same path and simply gets a sharper
   * cover out of it.
   *
   * **With the same caveat the Logistics and Manufacturing covers had, file for file.**
   * It arrived as two captures, "(1 of 2)" and "(2 of 2)", and they are not two halves
   * — they are overlapping crops of the same square artwork, one cut off at the bottom
   * through the logo and one carrying the whole design. The second is the one held
   * here, at 2000 × 1848 rather than square, so it is missing a sliver of the top
   * margin, and the index card's square crop trims about 4% off each side. `check` asks
   * for the file itself, which would replace this under the same key.
   */
  photo: {
    src: '/photo/daimi-report-2026-insurance-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Insurance Focus, February 2026',
  } satisfies Photo,
  check:
    'Three things. The three paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — including the 2.99, which the capture sets in bold and which is plain here. That 2.99 is Figure 1’s 2025 average too, so the page checks itself on that one, and it is above the 2.93 cross-industry baseline the Logistics cut prints, which is what makes “leads the cross-industry baseline” hold. Second, the live hero has no cover beside the words — the right of it is empty but for the ribbon — and the cover is there here because that is what every other designed report page on this site does, and because the 2000px file you supplied would otherwise only ever be seen as a 400px index card. Say if the live emptiness was the point. Third, the cover file itself is not whole: the two “scaled” captures you attached are overlapping crops of one square image, so the fuller one held here is 2000 × 1848 and loses a sliver of the margin above the title, with the index card trimming roughly 4% off each side to make it square again. Send the cover as a single file and it will replace this one under the same name.',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The live band is a lede paragraph followed by three lines that each open with a
 * bolded label — "Core Strengths:", "The Scaling Gap:", "Industrializing AI:". Split
 * into `label` and `body` here so the label can be set rather than emphasised inside a
 * sentence, which is what a `<strong>` in the middle of a paragraph gives.
 *
 * The heading is "DAIMI Findings" and nothing after it: like the Logistics and
 * Manufacturing cuts and unlike the Pharmaceuticals one, this page's live heading
 * carries no subtitle, so none is invented here.
 */
export const DAIMI_IS_FINDINGS = {
  heading: 'DAIMI Findings',
  lede: 'Insurance performance reflects an industry that is quantitatively strong but operationally uneven, with organizations often clustering around mid-level maturity.',
  points: [
    {
      label: 'Core Strengths',
      body: 'Maturity is highest in underwriting models, actuarial processes, and fraud analytics, where the work is quantitative and well-governed.',
    },
    {
      label: 'The Scaling Gap',
      body: 'Momentum is consistently hindered by legacy core platforms and fragmented customer views that limit straight-through processing and slow new product rollouts.',
    },
    {
      label: 'Industrializing AI',
      body: 'While many insurers possess advanced models, the challenge remains in redesigning workflows so that analytics insights consistently drive automated action across the value chain.',
    },
  ],
  /**
   * Figure 1 itself — the six-year study-results chart supplied with this request, the
   * same shape the Logistics, Manufacturing, Automotive, Banking, Services, Retail and
   * Pharmaceuticals cuts carry rather than the vitality scatter the Technology, Tourism
   * and Public Services cuts do or the portrait score range the Utilities cut does. Six
   * years, 2020 to 2025, each one a range from the laggard through the average to the
   * leader with that year's company count in the header. It is 1200 × 622 on a white
   * ground, so it gets a white card and fills it whole.
   */
  photo: {
    src: '/photo/daimi-insurance-study-results.webp',
    alt: 'DAIMI study results for the insurance industry, 2020 to 2025: the laggard, average and leader score each year, from 2.10–2.70–3.35 in 2020 to 2.39–2.99–3.58 in 2025',
  } satisfies Photo,
  caption:
    'Figure 1: Digital & AI Maturity Index (DAIMI) Study Results: Insurance Industry Score, Averages, Leaders, and Laggards',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'Figure 1 is the file you supplied and is shown whole, including its own year-by-year company counts (3 in 2020 rising to 121 in 2025) — the live caption carries neither those nor a year, so they are stated in the alt text and nowhere else. Its 2025 average, 2.99, is the number the hero opens with, which is the one cross-check the page can do on itself. Two readings the chart supports and the prose does not mention: the average has been flat at 2.99 for two years running while the leader kept climbing to 3.58, and the laggard line is the volatile one — 2.65 in 2023, then 1.90 in 2024 and 2.39 in 2025, still below where it stood in 2023. That is a sharper picture of “operationally uneven” than the lede gives, and it is also the widest leader-to-laggard spread in the six years. What wants a reading against digitopia.co is the prose: the lede and the three findings are transcribed from the capture, where body copy renders a few pixels tall.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_IS_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Insurance',
  body: [
    'To lead in 2026, insurance executives must move beyond siloed initiatives and focus on value-stream transformation. Success requires a disciplined effort to modernize core billing and policy platforms while embedding AI into decision workflows to improve cycle times and loss ratios.',
    'Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap to turn risk-ready foundations into a sustained competitive advantage.',
  ],
  /**
   * The mockup supplied with this request — the Insurance Focus cover, not the Future
   * 2030 Insurance outlook's, which this site already holds separately as
   * `/photo/future-2030-outlook-insurance-mockup.webp`. Unlike the Manufacturing cut and
   * like most of the others, the reference capture does *not* show it: the right of the
   * live analysis band is empty. It is here because `pages/FocusPage.tsx` draws it on
   * every cut of this shape, and `check` keeps that a question rather than a decision
   * made quietly.
   */
  photo: {
    src: '/photo/daimi-report-2026-insurance-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Insurance Focus',
  } satisfies Photo,
  check:
    'Two things. The paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — the first one in particular, which is the page’s argument in one sentence. And the mockup beside them is an addition: on the live page this band is words on the left and nothing on the right, and the mockup you attached is otherwise never shown at all, so it fills that column here the way it does on the other focus pages. It is also the smallest file of the three, 400 × 326, so it is capped at `max-w-sm` and cannot grow past it without showing soft — send it larger if this column should carry more weight. Say if the live emptiness was the point.',
}

/*
 * The methodology and global-perspective bands are not written out here. They are
 * `content/reportClosing.ts`, drawn by `components/ReportClosing.tsx` on every report
 * page but three — and the reference capture for this page shows exactly that pair,
 * pointing at the Digital & AI Maturity Report 2026, which is the "2024 değil 2026" the
 * request asked for. Nothing to change: the shared band already links to `DAIMI_SLUG`,
 * and its opening sentence already names this cut "The Insurance Focus Report" — the
 * capture says "The Insurance Focus Report" too, which is the `shortName` `focus()`
 * mints.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_IS_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Insurance Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `daimiChecks()` and `daimiLogisticsChecks()` use, and
 * appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut arrived
 * with both of its figures and its cover. Three of the notes are a transcription nobody
 * has read against the original, and one is a decision about gating that belongs to
 * marketing.
 */
export function daimiInsuranceChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_IS_INTRO.check },
    { section: 'findings', check: DAIMI_IS_FINDINGS.check },
    { section: 'analysis', check: DAIMI_IS_ANALYSIS.check },
    { section: 'download', check: DAIMI_IS_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_IS_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
